/**
 * Admission consent form PDF — renders the printable consent form with
 * checkbox clauses from a template config.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetails } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  CheckboxField,
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type {
  AdmissionConsentConfig,
  AdmissionConsentFields,
} from '@/frontend/features/printing/documents/consent/admission-template.types'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

const styles = StyleSheet.create({
  ...baseStyles,
  clauseItem: { marginBottom: mm(4) },
})

export function AdmissionConsentPdf({
  config,
  context,
  fields,
}: {
  config: AdmissionConsentConfig
  context: DocumentContext
  fields: AdmissionConsentFields
}) {
  const { patient, owner, clinic } = context

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>{config.title}</Text>

        <OwnerDetails owner={owner} />
        <View style={styles.divider} />
        <PatientDetails
          patient={patient}
          weight={context.weight}
          showDob
          showWeight
          showMicrochip
        />
        <View style={styles.divider} />

        <View style={styles.sectionGap}>
          <WithInlineResponse response={<FillField value={fields.weightToday || undefined} />}>
            <Text style={styles.bold}>Weight today (kg):</Text>
          </WithInlineResponse>
          <Spacer height={mm(3)} />
          <WithInlineResponse response={<FillField value={fields.allergies || undefined} />}>
            <Text style={styles.bold}>Allergies:</Text>
          </WithInlineResponse>
          <Spacer height={mm(3)} />
          <WithInlineResponse
            response={<FillField value={fields.currentMedications || undefined} />}
          >
            <Text style={styles.bold}>Current Medications:</Text>
          </WithInlineResponse>
          <Spacer height={mm(3)} />
          <WithInlineResponse response={<FillField value={fields.procedure || undefined} />}>
            <Text style={styles.bold}>PROCEDURE:</Text>
          </WithInlineResponse>
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            I consent to, and understand the following relating to my pet:
          </Text>
          <Spacer height={mm(3)} />
          {config.clauses.map((clause) => (
            <View key={clause.slice(0, 40)} style={styles.clauseItem}>
              <CheckboxField checked={false} label={clause} />
            </View>
          ))}
        </View>

        <OwnerSignature owner={owner} label="Owner or Authorised Agent" prefillName={false} />

        <View style={styles.divider} />

        <VetSignoff vetName={fields.vetName} label="ADMITTED BY" />
      </Page>
    </Document>
  )
}
